import React, { useEffect, useState } from 'react'
import { Button, FormControl, IconButton } from '@material-ui/core'
import { injectIntl } from 'react-intl'
import cn from 'classnames'
import CloseIcon from '@material-ui/icons/Close'
import isEmpty from 'lodash/isEmpty'
import ArrowIcon from '@material-ui/icons/ArrowDropDown'
import parse from 'html-react-parser'
import { TABLET_BREAKPOINT } from 'helpers/responsive'
import { getFormattedDate, getDateFormat } from 'helpers/dates'
import SidePopover from 'components/common/Popover/SidePopover'
import ImageModal from 'components/common/Modals/ImageModal'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import { ReactComponent as AddIcon } from 'assets/img/add-button.svg'
import { ReactComponent as RemoveIcon } from 'assets/img/remove-button.svg'
import { ReactComponent as AddWithDataIcon } from 'assets/img/add-with-data-button.svg'
import withPromise from 'helpers/withPromise'
import {
  ACTION_DUPLICATE,
  ACTION_DUPLICATE_WITH_DATA,
  DUPLICATE_INSERT_PARAMETER_AFTER,
} from 'constants/index'
import JobField from '../JobFields'
import JobTaskPolicies from './JobTaskPolicies'
import messages from '../translate'
import BulkUnitsSelect from './BulkUnitsSelect'
import BulkTaskHeader from './BulkTaskHeader'

function JobTaskBulkRun({
  activeUnitId,
  findInputData,
  fieldState,
  onJobTaskAutoSave,
  onJobTaskValueRange,
  onJobTaskFocusedInput,
  availableInputs,
  locusList,
  input_items,
  outputs,
  needs_locus,
  needs_locus_group,
  locusGroup,
  locus,
  setCurrentTaskUnits,
  setMultiTaskUnits,
  setMultiChoiceMode,
  setCurrentTask,
  evt_tasks,
  locusGroupList,
  handleUnitsChange,
  checkData,
  policy_indicators,
  currentTask,
  findChildren,
  resetInvalidDates,
  id: currentTaskId,
  intl: { formatMessage },
  activeJob: { id: activeJobId },
  deleteDuplicatedInputGroup,
  fetchJobTaskList,
  multiChoiceMode,
  handleDependentLookupInputs,
  multiUnitTaskIds,
  handleDuplicateInputGroup,
  clearCurrentTask,
  evtInputs,
  onJobTaskListRefresh,
  licensee: { usaDateFormat },
}) {
  let inputGroupIndex = 1
  const [showSidePopover, setShowSidePopover] = useState(false)
  const [unitsName, setUnitsName] = useState([])
  const [unitsIds, setUnitsIds] = useState({})
  const [unitGroupsName, setUnitGroupsName] = useState([])
  const [unitGroupsIds, setUnitGroupsIds] = useState([])
  const [locusGroupListState, setLocusGroupListState] = useState(locusGroupList)
  const isGroupList = needs_locus_group && !needs_locus
  const isLocusList = !needs_locus_group && needs_locus
  const isLocusAndGroupList = needs_locus_group && needs_locus
  const dateFormat = getDateFormat(usaDateFormat)

  const renderOutputs = () => (
    <div className="job-outputs-list">
      {outputs.map(({ values, id, title }) => {
        const { value, date_updated } =
          (values.length &&
            values.find(({ locus: currentLocus, locus_group }) => {
              if (currentLocus && locus_group) {
                return currentLocus === locus && locus_group === locusGroup
              }

              if (currentLocus && !locus_group) {
                return currentLocus === 10
              }

              return locus_group === 10
            })) ||
          {}

        return (
          <div className="output" key={id}>
            <div>
              {`${title}: `}
              <b>{value || value === 0 ? parse(value) : 'N/A'}</b>
            </div>
            {!!date_updated && (
              <div className="date-updated">
                {`Update: ${getFormattedDate(date_updated, dateFormat)}`}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )

  const renderInput = (input, group) => {
    const {
      id,
      parameters: { dependency: { input: inputID } = {} },
    } = input
    const dependency = { value: '', title: '' }
    const inputs = input_items.filter(
      ({ input_item_type }) => input_item_type === 'input',
    )
    const inputGroups = input_items.filter(
      ({ input_item_type }) => input_item_type === 'input_group',
    )
    let inputGroupsInputs = []

    if (inputGroups.length) {
      inputGroups.forEach(({ inputs }) => {
        inputGroupsInputs = [...inputGroupsInputs, ...inputs]
      })
    }

    const allInputs = [...inputGroupsInputs, ...inputs]

    if (inputID) {
      const { title, type } = allInputs.find(({ id }) => id === inputID) || {}
      dependency.title = title
      dependency.type = type

      if (currentTask.evt_inputs) {
        const { result } =
          currentTask.evt_inputs.find(({ input }) => inputID === input) || {}

        if (result) {
          dependency.value = result
        }
      }
    }

    const filledChildren = findChildren(allInputs, currentTask, input.id)
    const firstEvtTaskMatch = multiUnitTaskIds.length
      ? evt_tasks.find(({ id }) => id === multiUnitTaskIds[0])
      : {}
    const evtTaskMatches = multiUnitTaskIds.length
      ? evt_tasks.filter(({ id: evtTaskId }) =>
          multiUnitTaskIds.find(id => id === evtTaskId),
        )
      : null

    return (
      <JobField
        key={id}
        index={id}
        className="job-field-main-box"
        inputInfo={input}
        disabled={fieldState}
        isUnitBlockOpen={showSidePopover}
        activeJobId={activeJobId}
        inputData={findInputData(id)}
        multiChoiceMode={multiChoiceMode}
        dependency={{
          dependency,
          children: filledChildren,
          currentEvtTask: isEmpty(currentTask)
            ? firstEvtTaskMatch || {}
            : currentTask,
          currentTaskID: currentTaskId,
          evtTaskMatches,
        }}
        onJobTaskAutoSave={onJobTaskAutoSave}
        onJobTaskValueRange={onJobTaskValueRange}
        onJobTaskFocusedInput={onJobTaskFocusedInput}
        resetInvalidDates={resetInvalidDates}
        handleDependentLookupInputs={handleDependentLookupInputs}
        clearCurrentTask={clearCurrentTask}
        inputs={
          group
            ? [...group.inputs, ...inputs]
            : [...inputGroupsInputs, ...inputs]
        }
      />
    )
  }

  const renderInputGroup = (group, duplicateMatches) => {
    const {
      title,
      inputs,
      duplicable,
      duplicate_context,
      id,
      original_input_group_id,
    } = group
    const isInputGroupValid =
      inputs.length &&
      (isEmpty(duplicate_context) ||
        // eslint-disable-next-line no-nested-ternary
        (needs_locus && needs_locus_group
          ? duplicate_context.locus === locus &&
            duplicate_context.locus_group === activeUnitId
          : needs_locus && !needs_locus_group
          ? duplicate_context.locus === activeUnitId
          : duplicate_context.locus_group === activeUnitId))
    let payloadLocus
    let payloadLocusGroup

    if (!original_input_group_id) {
      inputGroupIndex = 1
    } else if (isInputGroupValid) {
      inputGroupIndex += 1
    }

    if (needs_locus && needs_locus_group) {
      payloadLocus = locus
      payloadLocusGroup = activeUnitId
    } else if (needs_locus && !needs_locus_group) {
      payloadLocus = activeUnitId
    } else {
      payloadLocusGroup = activeUnitId
    }

    return isInputGroupValid ? (
      <section className="input-group" key={id}>
        {!original_input_group_id && <div className="title">{title}</div>}
        <div className="input-container">
          {(!!duplicateMatches.length || !!original_input_group_id) && (
            <div className="input-group-index">{inputGroupIndex}</div>
          )}
          {!!original_input_group_id && (
            <IconButton
              size="small"
              className="remove-duplicate-button"
              onClick={() => {
                const previewInputs = inputs
                  .filter(({ preview }) => preview)
                  .map(({ id }) => id)
                const previewsToRemove = evtInputs
                  .filter(({ input }) => previewInputs.find(id => id === input))
                  .map(({ id }) => id)

                withPromise(deleteDuplicatedInputGroup, {
                  id,
                  previewsToRemove,
                }).then(response => {
                  if (!response.error) {
                    fetchJobTaskList({
                      activeJobId,
                    })

                    if (previewsToRemove.length) {
                      onJobTaskListRefresh()
                    }
                  }
                })
              }}>
              <RemoveIcon />
            </IconButton>
          )}
          {inputs.map(input => renderInput(input, group))}
        </div>
        {duplicable && (
          <div className="duplication-block">
            <IconButton
              size="small"
              className="duplicate-button"
              onClick={() => {
                if (!fieldState) {
                  handleDuplicateInputGroup(
                    DUPLICATE_INSERT_PARAMETER_AFTER,
                    ACTION_DUPLICATE_WITH_DATA,
                    id,
                    payloadLocus,
                    payloadLocusGroup,
                  )
                }
              }}>
              <AddWithDataIcon />
            </IconButton>
            <IconButton
              size="small"
              className="duplicate-button"
              onClick={() => {
                if (!fieldState) {
                  handleDuplicateInputGroup(
                    DUPLICATE_INSERT_PARAMETER_AFTER,
                    ACTION_DUPLICATE,
                    id,
                    payloadLocus,
                    payloadLocusGroup,
                  )
                }
              }}>
              <AddIcon />
            </IconButton>
          </div>
        )}
      </section>
    ) : null
  }

  const renderInputList = () =>
    input_items.map(item => {
      const { input_item_type, id, original_input_group_id } = item

      if (input_item_type === 'input') {
        return renderInput(item)
      }

      const duplicatedGroups = input_items.filter(
        group =>
          group.input_item_type === 'input_group' &&
          group.original_input_group_id &&
          (isEmpty(group.duplicate_context) ||
            // eslint-disable-next-line no-nested-ternary
            (needs_locus && needs_locus_group
              ? group.duplicate_context.locus === locus &&
                group.duplicate_context.locus_group === activeUnitId
              : needs_locus && !needs_locus_group
              ? group.duplicate_context.locus === activeUnitId
              : group.duplicate_context.locus_group === activeUnitId)),
      )
      const duplicateMatches = duplicatedGroups.filter(
        group =>
          id === group.original_input_group_id ||
          id === group.id ||
          original_input_group_id === group.original_input_group_id,
      )

      return renderInputGroup(item, duplicateMatches)
    })

  const renderTaskBulkHeader = () => (
    <div className="task-bulk-run-header">
      <div className="job-field-label">
        {formatMessage(messages.chooseLocus)}
      </div>
      <IconButton size="small" onClick={() => setShowSidePopover(false)}>
        <CloseIcon />
      </IconButton>
    </div>
  )

  const renderTaskBulkSearch = () => (
    <div className="task-bulk-run-header">
      <BulkTaskHeader
        evt_tasks={evt_tasks}
        input_items={input_items}
        locusGroupList={locusGroupList}
        setLocusGroupListState={setLocusGroupListState}
      />
      <IconButton
        size="small"
        className="task-bulk-run-button"
        onClick={() => {
          setMultiChoiceMode(!multiChoiceMode)
          setUnitsIds({})
          setUnitGroupsIds([])
          setUnitsName([])
          setUnitGroupsName([])
          setCurrentTask({})
          onJobTaskListRefresh(true)
        }}>
        {multiChoiceMode
          ? formatMessage(messages.cancel)
          : formatMessage(messages.select)}
      </IconButton>
    </div>
  )

  const selectedGroupUnitData = (groupUnits, remove) => {
    let newGroupUnits

    if (!remove) {
      newGroupUnits = [...new Set([...unitsName, ...groupUnits])]
    } else {
      newGroupUnits = unitsName.filter(item => !groupUnits.includes(item))
    }

    setUnitsName(newGroupUnits)
  }

  const selectedUnitName = (groupId, unitId) => {
    const groupUnits = unitsIds[groupId] || []
    const unitExist =
      !isEmpty(groupUnits) && groupUnits.some(id => id === unitId)
    const { name: unitName } =
      locusList.length && unitId
        ? locusList.find(({ id }) => id === unitId)
        : {}
    let newUnitsIds

    if (multiChoiceMode) {
      if (unitExist) {
        const newUnitsNameList = unitsName.filter(name => name !== unitName)
        const newGroupUnits = groupUnits.filter(id => id !== unitId)

        if (!isEmpty(newGroupUnits)) {
          newUnitsIds = {
            ...unitsIds,
            [groupId]: newGroupUnits,
          }
        } else {
          newUnitsIds = { ...unitsIds }
          delete newUnitsIds[groupId]
        }

        setUnitsName(newUnitsNameList)
      } else {
        newUnitsIds = {
          ...unitsIds,
          [groupId]: [...groupUnits, unitId],
        }

        setUnitsName([...unitsName, unitName])
      }
    } else {
      newUnitsIds = {
        [groupId]: [unitId],
      }

      setUnitsName([unitName])
    }

    setUnitsIds(newUnitsIds)

    return newUnitsIds
  }

  const selectedUnitGroupName = groupId => {
    const { name: unitGroupName } =
      locusGroupList.length && groupId
        ? locusGroupList.find(({ id }) => id === groupId)
        : {}
    let newUnitGroupsIds

    if (multiChoiceMode) {
      let newUnitsNameList = unitGroupsName.filter(
        name => name !== unitGroupName,
      )
      newUnitGroupsIds = unitGroupsIds.filter(id => id !== groupId)

      if (!unitGroupsIds.find(id => id === groupId)) {
        newUnitGroupsIds = [...unitGroupsIds, groupId]
        newUnitsNameList = [...unitGroupsName, unitGroupName]
      }

      setUnitGroupsName(newUnitsNameList)
    } else {
      newUnitGroupsIds = [groupId]

      setUnitGroupsName([unitGroupName])
    }

    setUnitGroupsIds(newUnitGroupsIds)

    return newUnitGroupsIds
  }

  useEffect(() => {
    if (!isEmpty(currentTask)) {
      const { locus, locus_group } = currentTask

      if (locus) {
        selectedUnitName(locus_group, locus)
      } else {
        selectedUnitGroupName(locus_group)
      }
    }
  }, [JSON.stringify(currentTask)])

  const taskGroupUnitsChange = (groupId, unitIdList, unitNameList) => {
    if (!multiChoiceMode) {
      return
    }

    const groupIdList =
      unitsIds && Object.keys(unitsIds).map(unit => Number(unit))

    if (!isEmpty(groupIdList) && groupIdList.includes(groupId)) {
      const newUnitGroupIds = { ...unitsIds }

      delete newUnitGroupIds[groupId]

      setUnitsIds(newUnitGroupIds)
      selectedGroupUnitData(unitNameList, true)
    } else {
      setUnitsIds({
        ...unitsIds,
        [groupId]: unitIdList,
      })

      selectedGroupUnitData(unitNameList)
    }
  }

  const taskUnitsChange = (groupId, unitId) => {
    const groupIds = Object.keys(unitsIds)

    if (unitId) {
      selectedUnitName(groupId, unitId)
    } else {
      selectedUnitGroupName(groupId)
    }

    if (!multiChoiceMode) {
      const differentGroupId = isEmpty(groupIds) || groupIds[0] !== groupId

      if (groupId && differentGroupId) {
        setCurrentTaskUnits('locusGroup', groupId, false, !unitId)
      }

      if (unitId) {
        setCurrentTaskUnits('locus', unitId, false, true)
      }

      if (differentGroupId) {
        setUnitsIds({
          ...unitsIds,
          [groupId]: [],
        })
      }

      setShowSidePopover(false)
      handleUnitsChange(groupId)
    }
  }

  const renderLocusUnits = item => (
    <BulkUnitsSelect
      key={item.id}
      data={item}
      multiChoice={multiChoiceMode}
      unitsIds={unitsIds}
      unitGroupsIds={unitGroupsIds}
      input_items={input_items}
      needs_locus={needs_locus}
      needs_locus_group={needs_locus_group}
      evt_tasks={evt_tasks}
      handleUnitsChange={taskUnitsChange}
      handleGroupUnitsChange={taskGroupUnitsChange}
    />
  )

  const renderGroupUnits = item => (
    <BulkUnitsSelect
      key={item.id}
      data={item}
      multiChoice={multiChoiceMode}
      unitsIds={unitGroupsIds}
      unitGroupsIds={unitGroupsIds}
      input_items={input_items}
      needs_locus={needs_locus}
      needs_locus_group={needs_locus_group}
      evt_tasks={evt_tasks}
      handleUnitsChange={taskUnitsChange}
      handleGroupUnitsChange={taskGroupUnitsChange}
    />
  )

  const unitValueList = namesList => namesList.map(name => name).join(', ')

  return (
    <div>
      <FormControl fullWidth>
        <span className="job-field-bulk-label">
          {formatMessage(messages.chooseLocus)}
        </span>
        <div className="job-field-main-box">
          <div
            role="presentation"
            onClick={() => setShowSidePopover(true)}
            className="job-field-input-bulk job-field-input">
            {evt_tasks && (
              <>
                <span className="job-field-label">
                  {needs_locus_group && !needs_locus
                    ? unitValueList(unitGroupsName)
                    : unitValueList(unitsName)}
                </span>
                <ArrowIcon className="job-field-icon" />
              </>
            )}
          </div>
          {ifWindowWidthLessThan(TABLET_BREAKPOINT) ? (
            <SidePopover
              open={showSidePopover}
              close={() => setShowSidePopover(false)}
              className="job-field-input-image-popover"
              position="bottom">
              <div className="task-bulk-run">
                <div className="task-bulk-run-popover">
                  {renderTaskBulkHeader()}
                  {renderTaskBulkSearch()}
                  <div className="task-bulk-run-content">
                    {isGroupList &&
                      locusGroupListState.map(unit => renderGroupUnits(unit))}
                    {isLocusList &&
                      locusList.map(unit => renderLocusUnits(unit))}
                    {isLocusAndGroupList &&
                      locusGroupListState.map(unit => renderLocusUnits(unit))}
                  </div>
                </div>
              </div>
            </SidePopover>
          ) : (
            <ImageModal
              open={showSidePopover}
              onClose={() => setShowSidePopover(false)}>
              <div className="task-bulk-run">
                <div className="task-bulk-run-modal">
                  {renderTaskBulkHeader()}
                  {renderTaskBulkSearch()}
                  <div className="task-bulk-run-content">
                    {isGroupList &&
                      locusGroupListState.map(unit => renderGroupUnits(unit))}
                    {isLocusList &&
                      locusList.map(unit => renderLocusUnits(unit))}
                    {isLocusAndGroupList &&
                      locusGroupListState.map(unit => renderLocusUnits(unit))}
                  </div>
                  {multiChoiceMode && (
                    <Button
                      fullWidth
                      href={null}
                      color="primary"
                      size="large"
                      variant="contained"
                      type="button"
                      onClick={() => {
                        setShowSidePopover(false)
                        setMultiTaskUnits(
                          needs_locus ? unitsIds : unitGroupsIds,
                        )
                      }}>
                      {`${
                        needs_locus ? unitsName.length : unitGroupsName.length
                      } ${formatMessage(messages.itemsSelected)}`}
                    </Button>
                  )}
                </div>
              </div>
            </ImageModal>
          )}
        </div>
      </FormControl>
      {availableInputs && (
        <div className={cn({ 'job-task-completed': fieldState })}>
          <JobTaskPolicies
            needsLocus={needs_locus}
            needsLocusGroup={needs_locus_group}
            policyIndicators={policy_indicators}
          />
          {checkData(outputs) && renderOutputs()}
          {renderInputList()}
        </div>
      )}
    </div>
  )
}

export default injectIntl(JobTaskBulkRun)
